import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, Users, Calendar, Package, TrendingUp } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * HostRollupSummary - per-host analytics summary across 4 buckets (R per-host Analytics).
 * Renders a 4-tab bar (Clients / Bookings & Revenue / Products / Brand Snapshot).
 * Reads /api/host/analytics/rollup which returns rolled-up counts.
 */
export default function HostRollupSummary({ eventId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("session_token");
    if (!token) { setLoading(false); return; }
    fetch(`${API}/host/analytics/rollup${eventId ? `?event_id=${eventId}` : ""}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => setData(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [eventId]);

  if (loading) return (
    <Card><CardContent className="p-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></CardContent></Card>
  );
  if (!data) return null;

  const { clients, bookings, products, brand } = data;

  return (
    <Card data-testid="host-rollup-summary">
      <CardContent className="p-4 sm:p-6">
        <Tabs defaultValue="clients">
          <TabsList className="grid grid-cols-4 mb-4">
            <TabsTrigger value="clients" data-testid="rollup-tab-clients"><Users className="w-3.5 h-3.5 mr-1.5" /> Clients</TabsTrigger>
            <TabsTrigger value="bookings" data-testid="rollup-tab-bookings"><Calendar className="w-3.5 h-3.5 mr-1.5" /> Bookings</TabsTrigger>
            <TabsTrigger value="products" data-testid="rollup-tab-products"><Package className="w-3.5 h-3.5 mr-1.5" /> Products</TabsTrigger>
            <TabsTrigger value="brand" data-testid="rollup-tab-brand"><TrendingUp className="w-3.5 h-3.5 mr-1.5" /> Brand</TabsTrigger>
          </TabsList>

          <TabsContent value="clients">
            <div className="grid grid-cols-2 gap-3">
              <Stat label="Unique clients" value={clients?.unique || 0} />
              <Stat label="VIP" value={clients?.vip || 0} />
            </div>
          </TabsContent>
          <TabsContent value="bookings">
            <div className="grid grid-cols-3 gap-3">
              <Stat label="Booked" value={bookings?.booked || 0} />
              <Stat label="Complete" value={bookings?.complete || 0} />
              <Stat label="Revenue" value={`$${(bookings?.revenue || 0).toLocaleString()}`} />
            </div>
          </TabsContent>
          <TabsContent value="products">
            <div className="grid grid-cols-2 gap-3">
              <Stat label="Owned products" value={products?.owned || 0} />
              <Stat label="Clicks" value={products?.clicks || 0} />
            </div>
          </TabsContent>
          <TabsContent value="brand">
            <div className="grid grid-cols-1 gap-3">
              <Stat label="Brand page views" value={brand?.views || 0} />
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-xl bg-gray-50 px-4 py-3" data-testid={`rollup-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-xs text-gray-500 uppercase tracking-wide mt-0.5">{label}</p>
    </div>
  );
}
